import _ from 'lodash';
import BaseModel from './BaseModel';
import moment from 'moment';
import type { Moment } from 'moment';

/** Class representing a setting. */
class Setting extends BaseModel {
    _id: string;
    auth: {
        password: {
            enabled: boolean;
        },
        openid: {
            enabled: boolean;
            configuration_url: string;
            client_id: string;
            /** Write-only — never returned by the API. */
            client_secret?: string;
            hasClientSecret?: boolean;
            provisioning_mode?: 'invite_only' | 'jit';
            jit_default_role?: string;
        }
    };
    openai: {
        /** Write-only — never returned by the API. */
        api_key?: string;
        hasApiKey?: boolean;
        model: string;
    };
    apprise: {
        signal: {
            enabled: boolean;
            host: string;
            port: number;
            username: string;
        },
        email: {
            enabled: boolean;
            host: string;
            port: number;
            secure: boolean;
            auth: {
                user: string;
                /** Write-only — never returned by the API. */
                pass?: string;
                hasPassword?: boolean;
            }
            from: string;
        }
    };
    ticketing: {
        enabled: boolean;
        ticket_url: string;
        reply_url: string;
        close_url: string;
    };
    documentations: {
        local: string;
    };
    outline: {
        enabled: boolean;
        /** Write-only — never returned by the API. */
        api_key?: string;
        hasApiKey?: boolean;
        url: string;
        rootDocumentId: string;
    };
    gitlab: {
        enabled: boolean;
        user: string;
        url: string;
        /** Write-only — never returned by the API. */
        token?: string;
        hasToken?: boolean;
        oauth: {
            client_id: string;
            client_secret: string;
        };
        merge_requests: {
            squash: boolean;
            squash_commit_message: 'first_commit' | 'gitlab_default';
            remove_source_branch: boolean;
        };
    };
    metabase: {
        enabled: boolean;
        host: string;
        static_embed: {
            /** Write-only — never returned by the API. */
            secret_key?: string;
            hasSecretKey?: boolean;
            project_dashboard_id: number;
            theme: {
                dark: boolean;
                transparent_bg: boolean;
                bordered: boolean;
                titled: boolean;
            }
        }
    };
    languagetool: {
        enabled: boolean;
        api_url: string;
        default_language: string;
    };
    mileage: {
        enabled: boolean;
        rate_per_km: number;
        currency: string;
        generation_day: number;
        timezone: string;
        project: string | null;
        board: string | null;
        column: string | null;
        assignees: string[];
        card_title_template: string;
        include_detail_in_description: boolean;
    };
    sentry: {
        enabled: boolean;
        user: string;
        host: string;
        organization: string;
        /** Write-only — never returned by the API. */
        client_secret?: string;
        hasClientSecret?: boolean;
        /** Write-only — never returned by the API. */
        api_key?: string;
        hasApiKey?: boolean;
    };
    client_area: {
        enabled: boolean;
        api_url: string;
        /** Write-only — never returned by the API. */
        api_key?: string;
        hasApiKey?: boolean;
    };
    email_intake: {
        enabled: boolean;
        default_project: string;
        default_type: string;
        default_estimate: number;
        default_deadline: number;
        ack?: {
            enabled: boolean;
            subject: string;
            body: string;
            /** Newline-separated in the form; converted to string[] on save. */
            suppress_to: string;
        };
        /**
         * Spam allowlist (newline-separated in the form; converted to string[] on save).
         */
        spam_allowlist?: {
            emails: string;
            domains: string;
        };
        /** typeSubtype → SMTP From (e.g. Support Net Oxygen <support@…>). */
        from_by_type?: Record<string, string>;
    };
    signal_intake: {
        enabled: boolean;
        reaction_emoji: string;
        message_retention_days: number;
        /** Fallback Yoda project for unmapped DM contacts. */
        default_project: string;
    };
    rt: {
        enabled: boolean;
        base_url: string;
        /** Write-only — never returned by the API. */
        token?: string;
        hasToken?: boolean;
        username: string;
        /** Write-only — never returned by the API. */
        password?: string;
        hasPassword?: boolean;
        migrate_comment_template: string;
        migrate_comment_marker: string;
        default_post_import_status: string;
        /** Comma-separated in the form; converted to string[] on save. */
        statuses: string;
        ticket_path_template: string;
        fetch_concurrency: number;
        timeout_ms: number;
    };
    reminders: {
        frequency: string;
    };
    trash_retention: {
        enabled: boolean;
        value: number;
        unit: 'days' | 'months' | 'years';
    };
    sprints: {
        start: { weekday: number; time: string };
        end: { weekday: number; time: string };
        timezone: string;
        horizon: number;
        default_resources: Array<{
            member: string;
            availability: { percentage: number };
        }>;
    };
    createdAt: Moment;
    updatedAt: Moment;

    constructor(properties: AnyObj) {
        super({});

        this._id            = properties._id;
        this.auth           = properties.auth || {};
        this.openai         = properties.openai || {};
        this.apprise        = properties.apprise || {};
        this.ticketing      = properties.ticketing || {};
        this.documentations = properties.documentations || {};
        this.outline        = {
            enabled: false,
            api_key: '',
            url: '',
            rootDocumentId: '',
            ...(properties.outline || {}),
        };
        this.gitlab         = {
            enabled: false,
            user: '',
            url: '',
            token: '',
            ...(properties.gitlab || {}),
            oauth: {
                client_id: '',
                client_secret: '',
                ...(properties.gitlab?.oauth || {}),
            },
            merge_requests: {
                squash: true,
                squash_commit_message: 'first_commit',
                remove_source_branch: false,
                ...(properties.gitlab?.merge_requests || {}),
            },
        };
        this.metabase       = properties.metabase || {};
        this.languagetool   = {
            enabled: false,
            api_url: 'https://proofread.netoxygen.ch',
            default_language: 'auto',
            ...(properties.languagetool || {}),
        };
        const mileageIn = properties.mileage || {};
        this.mileage = {
            enabled: false,
            rate_per_km: 0.75,
            currency: 'CHF',
            generation_day: 20,
            timezone: 'Europe/Zurich',
            project: null,
            board: null,
            column: null,
            card_title_template: 'Notes de frais — {month_label}',
            include_detail_in_description: true,
            ...mileageIn,
            assignees: Array.isArray(mileageIn.assignees) ? mileageIn.assignees : [],
        };
        this.sentry         = properties.sentry || {};
        this.client_area    = properties.client_area || {};
        const emailIntakeIn = properties.email_intake || {};
        const ackIn = emailIntakeIn.ack || {};
        const suppressToRaw = ackIn.suppress_to;
        const suppressToText = Array.isArray(suppressToRaw)
            ? suppressToRaw.join('\n')
            : (suppressToRaw || '');
        const allowlistIn = (emailIntakeIn as AnyObj).spam_allowlist || {};
        const allowlistEmailsRaw = allowlistIn.emails;
        const allowlistDomainsRaw = allowlistIn.domains;
        const allowlistEmailsText = Array.isArray(allowlistEmailsRaw)
            ? allowlistEmailsRaw.join('\n')
            : (allowlistEmailsRaw || '');
        const allowlistDomainsText = Array.isArray(allowlistDomainsRaw)
            ? allowlistDomainsRaw.join('\n')
            : (allowlistDomainsRaw || '');
        const fromByTypeIn = (emailIntakeIn.from_by_type && typeof emailIntakeIn.from_by_type === 'object')
            ? emailIntakeIn.from_by_type as Record<string, string>
            : {};
        this.email_intake   = {
            enabled: false,
            default_project: '',
            default_type: 'support',
            default_estimate: 1,
            default_deadline: 7,
            ...emailIntakeIn,
            from_by_type: { ...fromByTypeIn },
            ack: {
                enabled: true,
                subject: '',
                body: '',
                ...ackIn,
                suppress_to: suppressToText,
            },
            spam_allowlist: {
                emails: allowlistEmailsText,
                domains: allowlistDomainsText,
            },
        };
        const signalIntakeIn = properties.signal_intake || {};
        this.signal_intake = {
            enabled: !!signalIntakeIn.enabled,
            reaction_emoji: String(signalIntakeIn.reaction_emoji || '🎟'),
            message_retention_days: Math.max(1, Number(signalIntakeIn.message_retention_days) || 7),
            default_project: String(signalIntakeIn.default_project || ''),
        };
        const rtIn = properties.rt || {};
        const statuses = Array.isArray(rtIn.statuses)
            ? rtIn.statuses.join(', ')
            : (rtIn.statuses || 'new, open, stalled, resolved, rejected, deleted');
        this.rt = {
            enabled: false,
            base_url: '',
            token: '',
            username: '',
            password: '',
            migrate_comment_template: 'Migré vers Yoda #%s — %s',
            migrate_comment_marker: 'Migré vers Yoda #',
            default_post_import_status: 'resolved',
            ticket_path_template: '/Ticket/Display.html?id=%s',
            fetch_concurrency: 5,
            timeout_ms: 30000,
            ...rtIn,
            statuses,
        };
        this.reminders      = properties.reminders || {};
        const trashIn = properties.trash_retention || {};
        const trashUnit = ['days', 'months', 'years'].includes(trashIn.unit)
            ? trashIn.unit
            : 'days';
        this.trash_retention = {
            enabled: !!trashIn.enabled,
            value: Math.max(1, Number(trashIn.value) || 30),
            unit: trashUnit,
        };
        const sprintsIn = properties.sprints || {};
        this.sprints = {
            start: {
                weekday: 1,
                time: '00:00',
                ...(sprintsIn.start || {}),
            },
            end: {
                weekday: 7,
                time: '23:59',
                ...(sprintsIn.end || {}),
            },
            timezone: sprintsIn.timezone || 'Europe/Zurich',
            horizon: Number(sprintsIn.horizon) || 8,
            default_resources: Array.isArray(sprintsIn.default_resources)
                ? sprintsIn.default_resources.map((r: AnyObj) => ({
                    member: typeof r.member === 'object' ? (r.member?._id || '') : (r.member || ''),
                    availability: {
                        percentage: Number(r.availability?.percentage ?? 100) || 100,
                    },
                }))
                : [],
        };
        this.createdAt      = moment(properties.createdAt);
        this.updatedAt      = moment(properties.updatedAt);
    }

    /**
     * Prepare this object for update.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForUpdate() : AnyObj {
        const prepared = _.cloneDeep(this) as AnyObj;

        prepared.auth.openid.enabled    = !!prepared.auth.openid.enabled;
        prepared.auth.password.enabled  = !!prepared.auth.password.enabled;
        prepared.auth.openid.provisioning_mode = prepared.auth.openid.provisioning_mode || 'jit';
        prepared.auth.openid.jit_default_role = prepared.auth.openid.jit_default_role || 'user';
        prepared.apprise.signal.enabled = !!prepared.apprise.signal.enabled;
        prepared.apprise.email.enabled  = !!prepared.apprise.email.enabled;
        prepared.apprise.email.secure   = !!prepared.apprise.email.secure;
        prepared.ticketing.enabled      = !!prepared.ticketing.enabled;
        prepared.gitlab.enabled         = !!prepared.gitlab.enabled;
        prepared.gitlab.oauth = {
            client_id: String(prepared.gitlab?.oauth?.client_id || ''),
            client_secret: String(prepared.gitlab?.oauth?.client_secret || ''),
        };
        prepared.gitlab.merge_requests = {
            squash: prepared.gitlab?.merge_requests?.squash !== false,
            squash_commit_message: prepared.gitlab?.merge_requests?.squash_commit_message
                === 'gitlab_default'
                ? 'gitlab_default'
                : 'first_commit',
            remove_source_branch: !!prepared.gitlab?.merge_requests?.remove_source_branch,
        };
        prepared.sentry.enabled         = !!prepared.sentry.enabled;
        prepared.languagetool = prepared.languagetool || {
            enabled: false,
            api_url: 'https://proofread.netoxygen.ch',
            default_language: 'auto',
        };
        prepared.languagetool.enabled = !!prepared.languagetool.enabled;
        prepared.languagetool.api_url = String(prepared.languagetool.api_url || '').trim();
        prepared.languagetool.default_language = String(
            prepared.languagetool.default_language || 'auto'
        ).trim() || 'auto';
        prepared.mileage = prepared.mileage || {
            enabled: false,
            rate_per_km: 0.75,
            currency: 'CHF',
            generation_day: 20,
            timezone: 'Europe/Zurich',
            project: null,
            board: null,
            column: null,
            assignees: [],
            card_title_template: 'Notes de frais — {month_label}',
            include_detail_in_description: true,
        };
        prepared.mileage.enabled = !!prepared.mileage.enabled;
        prepared.mileage.rate_per_km = Number(prepared.mileage.rate_per_km);
        if (!Number.isFinite(prepared.mileage.rate_per_km) || prepared.mileage.rate_per_km < 0)
            prepared.mileage.rate_per_km = 0.75;
        prepared.mileage.currency = String(prepared.mileage.currency || 'CHF').trim() || 'CHF';
        prepared.mileage.generation_day = Math.min(
            28,
            Math.max(1, Number(prepared.mileage.generation_day) || 20)
        );
        prepared.mileage.timezone = String(prepared.mileage.timezone || 'Europe/Zurich').trim()
            || 'Europe/Zurich';
        prepared.mileage.project = String(prepared.mileage.project || '').trim() || null;
        prepared.mileage.board = String(prepared.mileage.board || '').trim() || null;
        prepared.mileage.column = String(prepared.mileage.column || '').trim() || null;
        prepared.mileage.assignees = Array.isArray(prepared.mileage.assignees)
            ? prepared.mileage.assignees.map(String).filter(Boolean)
            : [];
        prepared.mileage.card_title_template = String(
            prepared.mileage.card_title_template || 'Notes de frais — {month_label}'
        ).trim() || 'Notes de frais — {month_label}';
        prepared.mileage.include_detail_in_description =
            prepared.mileage.include_detail_in_description !== false;
        prepared.client_area.enabled    = !!prepared.client_area.enabled;
        prepared.email_intake = prepared.email_intake || {
            enabled: false,
            default_project: '',
            default_type: 'support',
            default_estimate: 1,
            default_deadline: 7,
            from_by_type: {},
            ack: {
                enabled: true,
                subject: '',
                body: '',
                suppress_to: '',
            },
            spam_allowlist: {
                emails: '',
                domains: '',
            },
        };
        prepared.email_intake.enabled = !!prepared.email_intake.enabled;
        prepared.email_intake.from_by_type = prepared.email_intake.from_by_type && typeof prepared.email_intake.from_by_type === 'object'
            ? prepared.email_intake.from_by_type
            : {};
        prepared.email_intake.ack = prepared.email_intake.ack || {
            enabled: true,
            subject: '',
            body: '',
            suppress_to: '',
        };
        prepared.email_intake.ack.enabled = prepared.email_intake.ack.enabled !== false;
        if (typeof prepared.email_intake.ack.suppress_to === 'string') {
            prepared.email_intake.ack.suppress_to = prepared.email_intake.ack.suppress_to
                .split(/[\n,]/)
                .map((entry: string) => entry.trim())
                .filter(Boolean);
        } else if (!Array.isArray(prepared.email_intake.ack.suppress_to)) {
            prepared.email_intake.ack.suppress_to = [];
        }
        const splitAllowlistLines = (raw: unknown): string[] => {
            if (typeof raw === 'string') {
                return raw
                    .split(/[\n,]/)
                    .map((entry: string) => entry.trim())
                    .filter(Boolean);
            }
            if (Array.isArray(raw))
                return raw.map((entry) => String(entry || '').trim()).filter(Boolean);
            return [];
        };
        prepared.email_intake.spam_allowlist = prepared.email_intake.spam_allowlist || {
            emails: [],
            domains: [],
        };
        prepared.email_intake.spam_allowlist = {
            emails: splitAllowlistLines(prepared.email_intake.spam_allowlist.emails),
            domains: splitAllowlistLines(prepared.email_intake.spam_allowlist.domains),
        };
        prepared.signal_intake = prepared.signal_intake || {
            enabled: false,
            reaction_emoji: '🎟',
            message_retention_days: 7,
            default_project: '',
        };
        prepared.signal_intake.enabled = !!prepared.signal_intake.enabled;
        prepared.signal_intake.reaction_emoji = String(prepared.signal_intake.reaction_emoji || '🎟').trim() || '🎟';
        prepared.signal_intake.message_retention_days = Math.max(
            1,
            Number(prepared.signal_intake.message_retention_days) || 7
        );
        prepared.signal_intake.default_project = String(prepared.signal_intake.default_project || '').trim() || null;
        // Never persist deploy secret from the UI (kept in YAML / Swarm).
        delete prepared.signal_intake.webhook_secret;
        prepared.rt = prepared.rt || {
            enabled: false,
            base_url: '',
            token: '',
            username: '',
            password: '',
            migrate_comment_template: 'Migré vers Yoda #%s — %s',
            migrate_comment_marker: 'Migré vers Yoda #',
            default_post_import_status: 'resolved',
            statuses: 'new, open, stalled, resolved, rejected, deleted',
            ticket_path_template: '/Ticket/Display.html?id=%s',
            fetch_concurrency: 5,
            timeout_ms: 30000,
        };
        prepared.rt.enabled = !!prepared.rt.enabled;
        prepared.rt.fetch_concurrency = Number(prepared.rt.fetch_concurrency) || 5;
        prepared.rt.timeout_ms = Number(prepared.rt.timeout_ms) || 30000;
        if (typeof prepared.rt.statuses === 'string') {
            prepared.rt.statuses = prepared.rt.statuses
                .split(',')
                .map((status: string) => status.trim())
                .filter(Boolean);
        } else if (!Array.isArray(prepared.rt.statuses)) {
            prepared.rt.statuses = ['new', 'open', 'stalled', 'resolved', 'rejected', 'deleted'];
        }

        prepared.trash_retention = prepared.trash_retention || {
            enabled: false,
            value: 30,
            unit: 'days',
        };
        prepared.trash_retention.enabled = !!prepared.trash_retention.enabled;
        prepared.trash_retention.value = Math.max(1, Number(prepared.trash_retention.value) || 30);
        prepared.trash_retention.unit = ['days', 'months', 'years'].includes(prepared.trash_retention.unit)
            ? prepared.trash_retention.unit
            : 'days';

        prepared.sprints = prepared.sprints || {
            start: { weekday: 1, time: '00:00' },
            end: { weekday: 7, time: '23:59' },
            timezone: 'Europe/Zurich',
            horizon: 8,
            default_resources: [],
        };
        prepared.sprints.start = prepared.sprints.start || { weekday: 1, time: '00:00' };
        prepared.sprints.end = prepared.sprints.end || { weekday: 7, time: '23:59' };
        prepared.sprints.start.weekday = Number(prepared.sprints.start.weekday) || 1;
        prepared.sprints.end.weekday = Number(prepared.sprints.end.weekday) || 7;
        prepared.sprints.horizon = Math.max(1, Math.min(52, Number(prepared.sprints.horizon) || 8));
        prepared.sprints.timezone = prepared.sprints.timezone || 'Europe/Zurich';
        prepared.sprints.default_resources = (prepared.sprints.default_resources || [])
            .map((r: AnyObj) => ({
                member: typeof r.member === 'object' ? r.member?._id : r.member,
                availability: {
                    percentage: Number(r.availability?.percentage ?? 100) || 100,
                },
            }))
            .filter((r: AnyObj) => !!r.member);

        // Secrets are write-only: empty means "keep existing" on the server.
        const omitEmptySecret = (obj: AnyObj | undefined, key: string, flagKey?: string) => {
            if (!obj || typeof obj !== 'object')
                return;
            if (flagKey)
                delete obj[flagKey];
            if (!obj[key])
                delete obj[key];
        };
        omitEmptySecret(prepared.auth?.openid, 'client_secret', 'hasClientSecret');
        omitEmptySecret(prepared.openai, 'api_key', 'hasApiKey');
        omitEmptySecret(prepared.apprise?.email?.auth, 'pass', 'hasPassword');
        omitEmptySecret(prepared.outline, 'api_key', 'hasApiKey');
        omitEmptySecret(prepared.gitlab, 'token', 'hasToken');
        omitEmptySecret(prepared.metabase?.static_embed, 'secret_key', 'hasSecretKey');
        omitEmptySecret(prepared.sentry, 'client_secret', 'hasClientSecret');
        omitEmptySecret(prepared.sentry, 'api_key', 'hasApiKey');
        omitEmptySecret(prepared.client_area, 'api_key', 'hasApiKey');
        omitEmptySecret(prepared.rt, 'token', 'hasToken');
        omitEmptySecret(prepared.rt, 'password', 'hasPassword');

        return prepared;
    }
}

export default Setting;
